import { useState, useEffect } from 'react';
import '../../../../css/freelancer-services.css';
import { getPlatformServicesByProfession, createProfileService, getProfileServices, updateProfileService, deleteProfileService } from '../../../../services/servicesService';

const MyServices = () => {
    const [showModal, setShowModal] = useState(false);
    const [availableServices, setAvailableServices] = useState([]);
    const [availableServicesBackup, setAvailableServicesBackup] = useState([]);
    const [profileServices, setProfileServices] = useState([]);
    const [selectedServiceId, setSelectedServiceId] = useState(null);
    const [customServiceTitle, setCustomServiceTitle] = useState('');
    const [serviceDescription, setServiceDescription] = useState('');
    const [isLoading, setLoading] = useState(true);
    const [editingService, setEditingService] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);


    const toggleModal = () => {
        setShowModal(!showModal);
        if (!showModal === false) {
            // Closing modal → reset edit state
            setEditingService(null);
            setSelectedServiceId(null);
            setCustomServiceTitle('');
            setServiceDescription('');
        }
    };

    // Fetch available platform services
    useEffect(() => {
        const fetchServices = async () => {
            try {
                const services = await getPlatformServicesByProfession();
                setAvailableServices(services);
                setAvailableServicesBackup(services); // keep full backup
            } catch (error) {
                console.error('Error fetching services:', error);
            }
        };
        fetchServices();
    }, []);

    // Fetch user's profile services
    useEffect(() => {
        const fetchProfileServices = async () => {
            try {
                setLoading(true);
                const services = await getProfileServices();
                setProfileServices(services);
            } catch (error) {
                console.error('Error fetching profile services:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProfileServices();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (editingService) {
            await handleUpdateService();
            return;
        }

        if (selectedServiceId && customServiceTitle) {
            return alert('Please either select a service or enter a custom service, not both.');
        }

        if (!selectedServiceId && !customServiceTitle) {
            return alert('Please select a service or enter a custom service title.');
        }

        // Check for duplicates
        if (selectedServiceId) {
            const alreadyAdded = profileServices.some(
                (s) => s.platform_service_title === availableServices.find(svc => svc.id === parseInt(selectedServiceId))?.title
            );
            if (alreadyAdded) return alert('You already have this service.');
        }

        if (customServiceTitle) {
            const duplicateCustom = profileServices.some(
                (s) => s.title.toLowerCase() === customServiceTitle.trim().toLowerCase()
            );
            if (duplicateCustom) return alert('You already have a service with this title.');
        }

        try {
            const payload = selectedServiceId
                ? { platform_service_id: parseInt(selectedServiceId), description: serviceDescription }
                : { title: customServiceTitle.trim(), description: serviceDescription, is_custom: true };

            const newProfileService = await createProfileService(payload);

            const serviceWithTitleAndDescription = {
                ...newProfileService,
                title: selectedServiceId
                    ? availableServices.find((s) => s.id === parseInt(selectedServiceId))?.title
                    : customServiceTitle.trim(),
                description: serviceDescription,
                platform_service_id: selectedServiceId ? parseInt(selectedServiceId) : null, // ✅ add this
            };

            // Show new service on top
            setProfileServices(prev => [serviceWithTitleAndDescription, ...prev]);

            // ✅ Remove from available services if platform service
            if (selectedServiceId) {
                setAvailableServices(prev =>
                    prev.filter(s => s.id !== parseInt(selectedServiceId))
                );
            }

            // Reset modal
            setSelectedServiceId(null);
            setCustomServiceTitle('');
            setServiceDescription('');
            toggleModal();
        } catch (error) {
            console.error('Error creating profile service:', error);
            alert('Failed to add service. Please try again.');
        }
    };

    const handleEditClick = (service) => {
        setEditingService(service);

        // Prefill fields
        if (service.platform_service_id) {
            setSelectedServiceId(service.platform_service_id);
            setCustomServiceTitle('');
        } else {
            setSelectedServiceId(null);
            setCustomServiceTitle(service.title);
        }

        setServiceDescription(service.description || '');

        setShowModal(true);
    };

    const handleUpdateService = async () => {
        if (!editingService) return;

        try {
            const payload = selectedServiceId
                ? { platform_service_id: parseInt(selectedServiceId), description: serviceDescription }
                : { title: customServiceTitle.trim(), description: serviceDescription, is_custom: true };

            const updated = await updateProfileService(editingService.id, payload);

            setProfileServices(prev =>
                prev.map(s => s.id === editingService.id ? { ...s, ...updated } : s)
            );

            toggleModal();
        } catch (error) {
            console.error("Error updating service:", error);
            alert("Failed to update service.");
        }
    };

    const handleDeleteService = async (service) => {
        const confirmDelete = window.confirm(`Delete "${service.title}"?`);
        if (!confirmDelete) return;

        try {
            await deleteProfileService(service.id);

            setProfileServices(prev => prev.filter(s => s.id !== service.id));
            setDropdownOpen(null);

            // Re-fetch available services
            const services = await getPlatformServicesByProfession();
            setAvailableServices(services);
        } catch (error) {
            console.error("Failed to delete service:", error);
            alert("Failed to delete service.");
        }
    };

    if (isLoading) return null;

    return (
        <div className="info-container">
            <div className="add-service-btn-wrapper">
                <button className="add-service-btn" onClick={toggleModal}>
                    + Add Service
                </button>
                <p style={{ color: "var(--text)", margin: 0 }}>
                    {profileServices.length} {profileServices.length === 1 ? "Service" : "Services"}
                </p>
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{editingService ? "Edit Service" : "Add New Service"}</h3>

                        <form onSubmit={handleSubmit}>
                            {(() => {
                                const filteredServices = availableServices.filter(
                                    (service) =>
                                        !profileServices.some(
                                            (ps) => ps.platform_service_id === service.id
                                        )
                                );

                                // -------------------------------------
                                // 1. ADDING a new service
                                // -------------------------------------
                                if (!editingService) {
                                    if (filteredServices.length > 0) {
                                        // Adding + still have platform services to choose from
                                        return (
                                            <label>
                                                Choose a service:
                                                <select
                                                    value={selectedServiceId || ""}
                                                    onChange={(e) => setSelectedServiceId(e.target.value)}
                                                >
                                                    <option value="">-- Select --</option>
                                                    {filteredServices.map((service) => (
                                                        <option key={service.id} value={service.id}>
                                                            {service.title}
                                                        </option>
                                                    ))}
                                                </select>

                                                <p style={{ marginTop: "8px", marginBottom: "0px" }}>
                                                    Or enter a custom service:
                                                </p>

                                                <input
                                                    type="text"
                                                    value={customServiceTitle}
                                                    onChange={(e) => setCustomServiceTitle(e.target.value)}
                                                    placeholder="Custom service title"
                                                />
                                            </label>
                                        );
                                    }

                                    // Adding a service but NO platform options left
                                    return (
                                        <label>
                                            Create a custom service:
                                            <input
                                                type="text"
                                                value={customServiceTitle}
                                                onChange={(e) => setCustomServiceTitle(e.target.value)}
                                                placeholder="Custom service title"
                                            />
                                        </label>
                                    );
                                }

                                // -------------------------------------
                                // 2. EDITING a service
                                // -------------------------------------
                                if (editingService.is_custom) {
                                    // Custom service → editable input
                                    return (
                                        <label>
                                            Service:
                                            <input
                                                type="text"
                                                value={customServiceTitle || editingService.title}
                                                onChange={(e) => setCustomServiceTitle(e.target.value)}
                                                placeholder="Service title"
                                            />
                                        </label>
                                    );
                                }

                                // Platform service → text only, not editable
                                return (
                                    <label>
                                        Service:
                                        <p style={{ padding: "8px 0", margin: 0, fontWeight: 500 }}>
                                            {editingService.title}
                                        </p>
                                    </label>
                                );
                            })()}

                            <label>
                                Description:
                                <textarea
                                    value={serviceDescription}
                                    onChange={(e) => setServiceDescription(e.target.value)}
                                    placeholder="Add a description for this service"
                                />
                            </label>

                            <div className="modal-actions">
                                <button type="submit">Save</button>
                                <button type="button" onClick={toggleModal}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="services-list">
                {profileServices.map((service, index) => (
                    <div key={service.id} className="service-card">
                        <h4>
                            {service.title}
                            {service.is_custom && <span className="custom-tag">Custom</span>}
                        </h4>

                        <div className='service-actions'>
                            <button
                                className="service-card-ellipsis"
                                onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}
                            >
                                <i className="fa-solid fa-ellipsis-vertical"></i>
                            </button>

                            {dropdownOpen === index && (
                                <div className="service-dropdown">
                                    <button
                                        onClick={() => handleEditClick(service)}
                                        className='service-dropdown-item'
                                    >
                                        <i className="fas fa-pen"></i> Edit
                                    </button>

                                    <button
                                        onClick={() => handleDeleteService(service)}
                                        className="service-dropdown-item"
                                    >
                                        <i className="fas fa-trash job-delete"></i> Delete
                                    </button>
                                </div>
                            )}
                        </div>

                        {service.description && <p>{service.description}</p>}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MyServices;